/**
 * Workflow Service - Phase 5 BPM Implementation
 * 
 * This service provides CRUD operations for workflow definitions following
 * the Phase 5 specification with node-based workflow structure.
 * 
 * Collection: workflow_definitions
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

/**
 * Generate unique workflow code (WF-001, WF-002, etc.)
 */
async function generateWorkflowCode() {
  try {
    const workflowsRef = collection(db, 'workflow_definitions');
    const q = query(workflowsRef, orderBy('workflow_code', 'desc'), limit(1));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return 'WF-001';
    }
    
    const lastCode = snapshot.docs[0].data().workflow_code || 'WF-000';
    const lastNumber = parseInt(lastCode.split('-')[1]) || 0;
    const nextNumber = (lastNumber + 1).toString().padStart(3, '0');
    
    return `WF-${nextNumber}`;
  } catch (error) {
    console.error('Error generating workflow code:', error);
    // Fallback to timestamp-based code
    return `WF-${Date.now()}`;
  }
}

/**
 * Create a new workflow definition
 */
export async function createWorkflowDefinition(workflowData, userId) {
  try {
    // Validate required fields
    if (!workflowData.workflow_name) {
      throw new Error('Workflow name is required');
    }
    
    if (!workflowData.workflow_type) {
      throw new Error('Workflow type is required');
    }
    
    // Generate workflow code
    const workflow_code = await generateWorkflowCode();
    
    // Prepare workflow definition document
    const workflowDefinition = {
      workflow_code,
      workflow_name: workflowData.workflow_name,
      workflow_type: workflowData.workflow_type, // "clinical" | "administrative" | "maintenance" | "emergency"
      workflow_version: workflowData.workflow_version || '1.0.0',
      
      // Description
      description: workflowData.description || '',
      purpose: workflowData.purpose || '',
      scope: workflowData.scope || '',
      applicable_departments: workflowData.applicable_departments || [],
      applicable_locations: workflowData.applicable_locations || [],
      
      // Trigger conditions
      triggers: workflowData.triggers || [],
      
      // Workflow structure
      nodes: workflowData.nodes || [],
      edges: workflowData.edges || [],
      
      // Risk adaptation
      risk_adaptations: workflowData.risk_adaptations || [],
      
      // Variables
      workflow_variables: workflowData.workflow_variables || [],
      
      // Validation rules
      validation_rules: workflowData.validation_rules || [],
      
      // Metadata
      status: workflowData.status || 'draft', // "draft" | "active" | "deprecated"
      created_by: userId,
      created_at: serverTimestamp(),
      updated_by: userId,
      updated_at: serverTimestamp(),
      approved_by: null,
      approved_at: null,
      
      // Analytics (initialize)
      execution_count: 0,
      success_rate: 0,
      average_duration_minutes: 0,
      last_executed: null
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, 'workflow_definitions'), workflowDefinition);
    
    console.log(`✅ Created workflow definition: ${workflow_code} (${docRef.id})`);
    
    return {
      id: docRef.id,
      ...workflowDefinition
    };
  } catch (error) {
    console.error('Error creating workflow definition:', error);
    throw error;
  }
}

/**
 * Get workflow definition by ID
 */
export async function getWorkflowDefinition(workflowId) {
  try {
    const docRef = doc(db, 'workflow_definitions', workflowId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`Workflow definition not found: ${workflowId}`);
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  } catch (error) {
    console.error('Error getting workflow definition:', error);
    throw error;
  }
}

/**
 * Get workflow definition by code
 */
export async function getWorkflowDefinitionByCode(workflowCode) {
  try {
    const workflowsRef = collection(db, 'workflow_definitions');
    const q = query(workflowsRef, where('workflow_code', '==', workflowCode));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error(`Workflow definition not found: ${workflowCode}`);
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting workflow definition by code:', error);
    throw error;
  }
}

/**
 * Get all workflow definitions with optional filters
 */
export async function getAllWorkflowDefinitions(filters = {}) {
  try {
    const workflowsRef = collection(db, 'workflow_definitions');
    let q = query(workflowsRef);
    
    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.workflow_type) {
      q = query(q, where('workflow_type', '==', filters.workflow_type));
    }
    
    // Order by creation date (newest first)
    q = query(q, orderBy('created_at', 'desc'));
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting workflow definitions:', error);
    throw error;
  }
}

/**
 * Update workflow definition
 */
export async function updateWorkflowDefinition(workflowId, updates, userId) {
  try {
    // First check if document exists
    const docRef = doc(db, 'workflow_definitions', workflowId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`Workflow definition not found: ${workflowId}`);
    }
    
    // Prepare update data - preserve existing fields that aren't being updated
    const existingData = docSnap.data();
    const updateData = {
      ...existingData,
      ...updates,
      updated_by: userId,
      updated_at: serverTimestamp()
    };
    
    // Ensure required fields are present
    if (!updateData.workflow_name) {
      throw new Error('Workflow name is required');
    }
    if (!updateData.workflow_type) {
      throw new Error('Workflow type is required');
    }
    if (!updateData.workflow_version) {
      updateData.workflow_version = existingData.workflow_version || '1.0.0';
    }
    
    // If status is being changed to 'active', check if approval is required
    if (updates.status === 'active' && updates.status !== undefined) {
      // Note: Approval logic can be added here if needed
    }
    
    await updateDoc(docRef, updateData);
    
    console.log(`✅ Updated workflow definition: ${workflowId}`);
    
    // Return updated document
    return await getWorkflowDefinition(workflowId);
  } catch (error) {
    console.error('Error updating workflow definition:', error);
    throw error;
  }
}

/**
 * Delete workflow definition
 * Now includes dependency checking - throws error if dependencies exist
 */
export async function deleteWorkflowDefinition(workflowId, force = false) {
  // Check dependencies before deletion
  if (!force) {
    const { dependencyCheckService } = await import('./dependencyCheckService');
    const dependencyCheck = await dependencyCheckService.checkWorkflowDeletion(workflowId);
    
    if (!dependencyCheck.canDelete && dependencyCheck.blockingDependencies.length > 0) {
      const errorMessages = dependencyCheck.blockingDependencies.map(d => d.message).join('; ');
      throw new Error(`Cannot delete workflow: ${errorMessages}`);
    }
  }

  try {
    const docRef = doc(db, 'workflow_definitions', workflowId);
    await deleteDoc(docRef);
    
    console.log(`✅ Deleted workflow definition: ${workflowId}`);
  } catch (error) {
    console.error('Error deleting workflow definition:', error);
    throw error;
  }
}

/**
 * Approve workflow definition
 */
export async function approveWorkflowDefinition(workflowId, approvedBy, userId) {
  try {
    const docRef = doc(db, 'workflow_definitions', workflowId);
    
    await updateDoc(docRef, {
      approved_by: approvedBy,
      approved_at: serverTimestamp(),
      status: 'active',
      updated_by: userId,
      updated_at: serverTimestamp()
    });
    
    console.log(`✅ Approved workflow definition: ${workflowId}`);
    
    return await getWorkflowDefinition(workflowId);
  } catch (error) {
    console.error('Error approving workflow definition:', error);
    throw error;
  }
}

/**
 * Create new version of workflow definition
 */
export async function createWorkflowVersion(workflowId, newVersionData, userId) {
  try {
    // Get original workflow
    const original = await getWorkflowDefinition(workflowId);
    
    // Generate new workflow code
    const workflow_code = await generateWorkflowCode();
    
    // Increment version number
    const versionParts = original.workflow_version.split('.');
    const major = parseInt(versionParts[0]) || 1;
    const minor = parseInt(versionParts[1]) || 0;
    const patch = parseInt(versionParts[2]) || 0;
    
    // Determine new version based on update type
    let newVersion;
    if (newVersionData.major_change) {
      newVersion = `${major + 1}.0.0`;
    } else if (newVersionData.minor_change) {
      newVersion = `${major}.${minor + 1}.0`;
    } else {
      newVersion = `${major}.${minor}.${patch + 1}`;
    }
    
    // Create new workflow definition with updated data
    const newWorkflow = {
      workflow_code,
      workflow_name: newVersionData.workflow_name || original.workflow_name,
      workflow_type: original.workflow_type,
      workflow_version: newVersion,
      
      // Copy existing data and apply updates
      description: newVersionData.description || original.description,
      purpose: newVersionData.purpose || original.purpose,
      scope: newVersionData.scope || original.scope,
      applicable_departments: newVersionData.applicable_departments || original.applicable_departments,
      applicable_locations: newVersionData.applicable_locations || original.applicable_locations,
      
      triggers: newVersionData.triggers || original.triggers,
      nodes: newVersionData.nodes || original.nodes,
      edges: newVersionData.edges || original.edges,
      risk_adaptations: newVersionData.risk_adaptations || original.risk_adaptations,
      workflow_variables: newVersionData.workflow_variables || original.workflow_variables,
      validation_rules: newVersionData.validation_rules || original.validation_rules,
      
      status: 'draft',
      created_by: userId,
      created_at: serverTimestamp(),
      updated_by: userId,
      updated_at: serverTimestamp(),
      approved_by: null,
      approved_at: null,
      
      // Reset analytics for new version
      execution_count: 0,
      success_rate: 0,
      average_duration_minutes: 0,
      last_executed: null
    };
    
    const docRef = await addDoc(collection(db, 'workflow_definitions'), newWorkflow);
    
    console.log(`✅ Created workflow version: ${workflow_code} v${newVersion} (${docRef.id})`);
    
    return {
      id: docRef.id,
      ...newWorkflow
    };
  } catch (error) {
    console.error('Error creating workflow version:', error);
    throw error;
  }
}

/**
 * Update workflow analytics after execution
 */
export async function updateWorkflowAnalytics(workflowId, executionResult) {
  try {
    const workflow = await getWorkflowDefinition(workflowId);
    
    const newExecutionCount = (workflow.execution_count || 0) + 1;
    const successCount = Math.round((workflow.success_rate || 0) / 100 * (newExecutionCount - 1));
    const newSuccessCount = executionResult.success ? successCount + 1 : successCount;
    const newSuccessRate = (newSuccessCount / newExecutionCount) * 100;
    
    const currentAvgDuration = workflow.average_duration_minutes || 0;
    const executionDuration = executionResult.duration_minutes || 0;
    const newAvgDuration = ((currentAvgDuration * (newExecutionCount - 1)) + executionDuration) / newExecutionCount;
    
    await updateDoc(doc(db, 'workflow_definitions', workflowId), {
      execution_count: newExecutionCount,
      success_rate: newSuccessRate,
      average_duration_minutes: newAvgDuration,
      last_executed: serverTimestamp()
    });
    
    console.log(`✅ Updated workflow analytics: ${workflowId}`);
  } catch (error) {
    console.error('Error updating workflow analytics:', error);
    // Don't throw - analytics update should not fail execution
  }
}

// Export service object for consistency
const workflowService = {
  createWorkflowDefinition,
  getWorkflowDefinition,
  getWorkflowDefinitionByCode,
  getAllWorkflowDefinitions,
  updateWorkflowDefinition,
  deleteWorkflowDefinition,
  approveWorkflowDefinition,
  createWorkflowVersion,
  updateWorkflowAnalytics
};

export default workflowService;

